import { useEffect, useMemo, useState } from "react"
import {
  Button,
  Drawer,
  Empty,
  Input,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from "antd"
import {
  DeleteOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons"
import apiClient from "../../Utils/apiClient"
import SelectBox from "../../components/SelectBox/SelectBox"

const { Paragraph, Text } = Typography
const { TextArea } = Input

const DEFAULTS = {
  enabled: true,
  minIntervalMinutes: 4,
  maxIntervalMinutes: 12,
  idleSkipSeconds: 90,
  keepDays: 14,
  excludedHostnames: [],
  vdiDevices: [],
  emeaDevices: [],
}

function normalizeSettings(row) {
  return {
    ...DEFAULTS,
    ...(row || {}),
    excludedHostnames: Array.isArray(row?.excludedHostnames)
      ? row.excludedHostnames
      : [],
    vdiDevices: Array.isArray(row?.vdiDevices) ? row.vdiDevices : [],
    emeaDevices: Array.isArray(row?.emeaDevices) ? row.emeaDevices : [],
  }
}

function mergeHosts(prev, hosts) {
  const seen = new Set(prev.map((item) => item.toLowerCase()))
  const next = [...prev]
  for (const host of hosts) {
    const value = String(host || "").trim()
    if (!value) continue
    const key = value.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    next.push(value)
  }
  return next
}

function parseHostnameText(text) {
  return String(text || "")
    .split(/[\s,;]+/)
    .map((item) => item.trim())
    .filter(Boolean)
}

export default function ActivityScreenshotSettings({ open, onClose }) {
  const [settings, setSettings] = useState(DEFAULTS)
  const [draftExcluded, setDraftExcluded] = useState([])
  const [draftEnabled, setDraftEnabled] = useState(true)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [pickerValue, setPickerValue] = useState(null)
  const [pasteText, setPasteText] = useState("")
  const [excludeFilter, setExcludeFilter] = useState("")

  const load = async () => {
    setLoading(true)
    try {
      const res = await apiClient("settings/activity-screenshots", "GET")
      if (res?.result) {
        const next = normalizeSettings(res.result)
        setSettings(next)
        setDraftEnabled(next.enabled !== false)
        setDraftExcluded(next.excludedHostnames)
      }
    } catch (err) {
      message.error(err.message || "Could not load screenshot settings")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!open) return
    setPickerValue(null)
    setPasteText("")
    setExcludeFilter("")
    load()
  }, [open])

  const filteredExcluded = useMemo(() => {
    const q = excludeFilter.trim().toLowerCase()
    if (!q) return draftExcluded
    return draftExcluded.filter((host) => host.toLowerCase().includes(q))
  }, [draftExcluded, excludeFilter])

  const save = async () => {
    setSaving(true)
    try {
      const res = await apiClient("settings/activity-screenshots", "PUT", {
        ...settings,
        enabled: draftEnabled,
        excludedHostnames: draftExcluded,
      })
      if (res?.success) {
        const next = normalizeSettings(res.result)
        setSettings(next)
        setDraftEnabled(next.enabled !== false)
        setDraftExcluded(next.excludedHostnames)
        message.success(res.message || "Screenshot settings saved")
        onClose?.(next)
      } else {
        message.error(res?.message || "Could not save screenshot settings")
      }
    } catch (err) {
      message.error(err.message || "Could not save screenshot settings")
    } finally {
      setSaving(false)
    }
  }

  const removeHost = (host) => {
    setDraftExcluded((prev) => prev.filter((item) => item !== host))
  }

  const addHosts = (hosts, label) => {
    const list = (hosts || []).map((row) =>
      typeof row === "string" ? row : row?.hostname
    )
    const before = draftExcluded.length
    const merged = mergeHosts(draftExcluded, list)
    setDraftExcluded(merged)
    const added = Math.max(0, merged.length - before)
    if (!added) {
      message.info(`No new ${label} devices to add`)
      return false
    }
    message.success(
      `Added ${added} ${label} device${added === 1 ? "" : "s"} to exclusions`
    )
    return true
  }

  const addFromPicker = (value) => {
    const host = typeof value === "string" ? value : value?.hostname
    if (!host) return
    addHosts([host], "device")
    // Keep the search box clear so another device can be added immediately.
    setPickerValue(null)
  }

  const addFromPaste = () => {
    const hosts = parseHostnameText(pasteText)
    if (!hosts.length) {
      message.info("Enter one or more hostnames to add")
      return
    }
    if (addHosts(hosts, "hostname")) setPasteText("")
  }

  const isExcluded = (hostname) =>
    draftExcluded.some(
      (host) => host.toLowerCase() === String(hostname || "").toLowerCase()
    )

  const emeaColumns = [
    {
      title: "Device",
      dataIndex: "hostname",
      width: 150,
      ellipsis: true,
      render: (value) => value || "—",
    },
    {
      title: "User",
      dataIndex: "user",
      width: 110,
      ellipsis: true,
      render: (value) => value || "—",
    },
    {
      title: "Timezone",
      dataIndex: "timezone",
      width: 140,
      ellipsis: true,
      render: (value, row) => value || row.country || "—",
    },
    {
      title: "",
      key: "add",
      width: 72,
      render: (_, row) =>
        row.hostname ? (
          <Button
            size="small"
            type="link"
            icon={<PlusOutlined />}
            disabled={isExcluded(row.hostname)}
            onClick={() => addHosts([row.hostname], "EMEA")}
          >
            {isExcluded(row.hostname) ? "Added" : "Add"}
          </Button>
        ) : null,
    },
  ]

  const excludeColumns = [
    {
      title: "Hostname",
      dataIndex: "hostname",
      ellipsis: true,
      render: (value) => value || "—",
    },
    {
      title: "",
      key: "remove",
      width: 88,
      align: "right",
      render: (_, row) => (
        <Button
          type="link"
          size="small"
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeHost(row.hostname)}
        >
          Remove
        </Button>
      ),
    },
  ]

  return (
    <Drawer
      title="Activity screenshots"
      placement="right"
      width={640}
      open={open}
      onClose={() => onClose?.(settings)}
      destroyOnClose={false}
      footer={
        <Space style={{ width: "100%", justifyContent: "space-between" }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {draftExcluded.length} excluded · Interval{" "}
            {settings.minIntervalMinutes}–{settings.maxIntervalMinutes} min ·
            idle {settings.idleSkipSeconds}s · keep {settings.keepDays}d
          </Text>
          <Space>
            <Button onClick={() => onClose?.(settings)}>Cancel</Button>
            <Button type="primary" loading={saving} onClick={save}>
              Save
            </Button>
          </Space>
        </Space>
      }
    >
      <Paragraph type="secondary" style={{ marginTop: 0 }}>
        Random desktop screenshots while someone is actively using the PC. Idle
        and lock screens are skipped. Excluded devices and VDI sessions never
        get screenshots; suspicious-input alerts can still go to IT.
      </Paragraph>

      <div className="shot-settings__row">
        <div>
          <Text strong>Random screenshots</Text>
          <div className="shot-settings__hint">
            {draftEnabled
              ? "On · excludes + VDI: no shots (IT alerts still OK) · list below"
              : "Off for everyone"}
          </div>
        </div>
        <Switch
          checked={draftEnabled}
          loading={loading}
          onChange={setDraftEnabled}
        />
      </div>

      <div className="shot-settings__block">
        <div className="shot-settings__row shot-settings__row--compact">
          <div>
            <Text strong>Exclude devices</Text>
            <div className="shot-settings__hint">
              Skip screenshots on these hostnames (suspicious-input IT alerts
              still send). Agents pick this up on the next check-in. Nothing is
              saved until you click Save.
            </div>
          </div>
          {draftExcluded.length ? (
            <Tag color="orange">{draftExcluded.length} excluded</Tag>
          ) : null}
        </div>

        <div className="shot-settings__add">
          <Text type="secondary" className="shot-settings__add-label">
            Search inventory
          </Text>
          <SelectBox
            className="w-100"
            style={{ width: "100%" }}
            name="hostname"
            optionValue="hostname"
            endPoint="devices/identifiers"
            placeholder="Search hostname, then select to add"
            value={pickerValue}
            onChange={addFromPicker}
          />
        </div>

        <div className="shot-settings__add">
          <Text type="secondary" className="shot-settings__add-label">
            Or paste hostnames
          </Text>
          <div className="shot-settings__paste-row">
            <TextArea
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              placeholder="One per line, or comma-separated"
              autoSize={{ minRows: 2, maxRows: 4 }}
              onPressEnter={(e) => {
                if (e.shiftKey) return
                e.preventDefault()
                addFromPaste()
              }}
            />
            <Button
              type="default"
              icon={<PlusOutlined />}
              onClick={addFromPaste}
              disabled={!pasteText.trim()}
            >
              Add
            </Button>
          </div>
        </div>

        <div className="shot-settings__exclude-toolbar">
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Filter excluded list"
            value={excludeFilter}
            onChange={(e) => setExcludeFilter(e.target.value)}
            style={{ maxWidth: 260 }}
          />
          <Space size="small">
            <Button
              size="small"
              onClick={() => addHosts(settings.emeaDevices, "EMEA")}
              disabled={!settings.emeaDevices?.length}
            >
              Add all EMEA
            </Button>
            <Button
              size="small"
              danger
              disabled={!draftExcluded.length}
              onClick={() => setDraftExcluded([])}
            >
              Clear all
            </Button>
          </Space>
        </div>

        {draftExcluded.length ? (
          <Table
            size="small"
            pagination={
              filteredExcluded.length > 12
                ? { pageSize: 12, size: "small", showSizeChanger: false }
                : false
            }
            rowKey={(row) => row.hostname}
            dataSource={filteredExcluded.map((hostname) => ({ hostname }))}
            columns={excludeColumns}
            locale={{
              emptyText: excludeFilter.trim()
                ? "No excluded devices match this filter"
                : "No devices excluded",
            }}
          />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No devices excluded yet"
          />
        )}
      </div>

      <div className="shot-settings__block">
        <Text strong>Currently in VDI (auto-skip)</Text>
        <Paragraph type="secondary" className="shot-settings__hint">
          No screenshots while the session is VDI (IT alerts can still send)
          {settings.vdiDevices?.length
            ? ` · ${settings.vdiDevices.length} now`
            : ""}
          .
        </Paragraph>
        {settings.vdiDevices?.length ? (
          <Table
            size="small"
            pagination={false}
            rowKey={(row) => row.hostname || row.user}
            dataSource={settings.vdiDevices.slice(0, 40)}
            scroll={{ y: 160 }}
            columns={[
              {
                title: "Device",
                dataIndex: "hostname",
                ellipsis: true,
                render: (value) => value || "—",
              },
              {
                title: "User",
                dataIndex: "user",
                width: 130,
                ellipsis: true,
                render: (value) => value || "—",
              },
              {
                title: "Status",
                key: "status",
                width: 100,
                render: () => <Tag color="blue">VDI skip</Tag>,
              },
            ]}
          />
        ) : (
          <div className="shot-settings__hint">
            {loading
              ? "Loading VDI sessions…"
              : "No devices currently in a VDI session."}
          </div>
        )}
      </div>

      <div className="shot-settings__block">
        <div className="shot-settings__row shot-settings__row--compact">
          <div>
            <Text strong>EMEA timezone devices</Text>
            <div className="shot-settings__hint">
              Reference list by laptop timezone (then location). Use Add or Add
              all EMEA above — nothing is excluded until you Save.
              {settings.emeaDevices?.length
                ? ` · ${settings.emeaDevices.length} devices`
                : ""}
            </div>
          </div>
        </div>
        {settings.emeaDevices?.length ? (
          <Table
            size="small"
            pagination={false}
            rowKey={(row) => row.hostname || row.user}
            dataSource={settings.emeaDevices.slice(0, 50)}
            scroll={{ y: 200 }}
            columns={emeaColumns}
          />
        ) : (
          <div className="shot-settings__hint">
            {loading
              ? "Loading EMEA devices…"
              : "No EMEA timezone / location devices found."}
          </div>
        )}
      </div>
    </Drawer>
  )
}
